import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import { showSideMenu } from "../../../../../App";
import AgentFunctions from "../../../../API/AgentFunctions";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import FunctionPreSelection from "./FunctionPreSelection";
import FunctionHiringTemplate from "./SubComponents/FunctionHiringTemplate";
import HiringTemplatesAPI from "../../../../API/HiringTemplates";

const FunctionsList = ({ isFunctionsLoading }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const { getPlanConfig, getActiveWorkspace } = useWorkspaces();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { user } = AppUser.useUser();
  const [showChoice, setShowChoice] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedFunctionId, setSelectedFunctionId] = React.useState(null);
  const [displayedFunctions, setDisplayedFunctions] = React.useState([]);
  const [selectedFunctionIds, setSelectedFunctionIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const [showPreSelection, setShowPreSelection] = useState(false);
  const [showHiringTemplate, setShowHiringTemplate] = useState(false);
  const [selectedGlobalTool, setSelectedGlobalTool] = useState(null);
  const history = useHistory();
  const params = useParams();
  useEffect(() => {
    const optId = params?.optId;
    if (!optId || optId === "hire") return;
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    if (isLoading) return;
    history.replace(`/workspaces/${wsId}/tools/${optId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.optId, isLoading, getActiveWorkspace()?.id]);
  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.name"),
      width: 0.25,
    },
    {
      title: t("settings.list.description"),
      // width: 0.25,
    },
    {
      title: t("settings.list.type"),
      width: 0.1,
    },
    // {
    //   title: "Authorisation Type",
    // },
    // {
    //   title: "Endpoint",
    //   width: 0.3,
    // },
    {
      title: t("settings.list.actions"),
      isAction: true,
      width: 0.1,
    },
  ];
  const loadAgentsFunctions = async (workspaceID) => {
    try {
      setIsLoading(true);
      const functionsData = await AgentFunctions.getAgentFunctionsByWorkspaceID(
        {
          workspaceID,
        },
      );
      let functions = functionsData?.data?.functionsByWorkspace?.items;
      functions =
        functions
          ?.map((u) => {
            if (u?._deleted !== true)
              return {
                ...u,
                parameters: JSON.parse(u.parameters),
                headers: u?.headers && JSON.parse(u.headers),
                staticArgs: u?.staticArgs && JSON.parse(u.staticArgs),
                // authorisationType: u?.authorisationType?.toUpperCase(),
                requestType: u?.requestType?.toUpperCase(),
              };
          })
          ?.filter((u) => u != null) || [];
      functions = functions.sort((a, b) => a.name.localeCompare(b.name));
      dispatch("SET_AGENTS_FUNCTIONS", functions || []);

      setDisplayedFunctions(functions);
    } catch (error) {
      AppToasty.show(t("failedToLoadFunctionsCaption"), {
        type: "danger",
      });
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    loadAgentsFunctions(state?.activeWorkspaceID);
  }, [state?.activeWorkspaceID]);
  useEffect(() => {
    setDisplayedFunctions([...state?.agentsFunctions]);
  }, [JSON.stringify([...state?.agentsFunctions])]);

  const identifyOptByID = (id) => {
    return state.agentsFunctions.find((u) => u.id === id);
  };
  const handleFunctionItemSelection = ({ item, index }) => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId || !item?.id) return;
    history.push(`/workspaces/${wsId}/tools/${item.id}`);
  };
  const handleFunctionItemDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      const _id = selectedFunctionId || selectedItem?.id;
      if (_id) {
        const _version =
          selectedItem?._version || identifyOptByID(_id)?._version;

        const result = await AgentFunctions.deleteAgentFunction(_id, _version);
        dispatch("SET_AGENTS_FUNCTIONS", [
          ...state?.agentsFunctions.filter((u) => u.id !== _id),
        ]);
        AppToasty.show(t("functionDeletionSuccessCaption"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedFunctionId(null);
        setSelectedItem(null);
      } else {
        throw new Error("No item selected");
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("functionDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleDuplication = async (id) => {
    try {
      let item = state?.agentsFunctions?.find((u) => u.id === id);
      if (!item) {
        AppToasty.show(t("functionDuplicationErrorCaption"), {
          type: "danger",
        });
        return;
      }
      let newFunction = {
        ...item,
        name: `${item.name} - Copy`,
        workspaceID: getActiveWorkspace()?.id,
      };
      // Remove system fields
      delete newFunction.id;
      delete newFunction.createdAt;
      delete newFunction.updatedAt;
      delete newFunction._version;
      delete newFunction._deleted;
      delete newFunction._lastChangedAt;
      delete newFunction.__typename;

      const result = await AgentFunctions.createAgentFunction(
        newFunction,
        user?.id,
      );
      if (result?.data?.createAgentFunction?.id) {
        // Reload the functions list
        const functionsData = await AgentFunctions.getAgentFunctionsByWorkspaceID(
          getActiveWorkspace()?.id,
        );
        const _functions =
          functionsData?.data?.functionsByWorkspace?.items?.filter(
            (u) => u?._deleted != true,
          ) || [];
        dispatch("SET_AGENTS_FUNCTIONS", _functions);
        AppToasty.show(t("functionDuplicationSuccessCaption"), {
          type: "success",
        });
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("functionDuplicationErrorCaption"), {
        type: "danger",
      });
    }
  };
  const handleBulkDeletion = async () => {
    try {
      for (const id of selectedFunctionIds) {
        const _version = identifyOptByID(id)?._version;
        await AgentFunctions.deleteAgentFunction(id, _version);
      }
      dispatch("SET_AGENTS_FUNCTIONS", [
        ...state?.agentsFunctions.filter(
          (u) => !selectedFunctionIds.includes(u.id),
        ),
      ]);
      setSelectedFunctionIds([]);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("functionDeletionSuccessCaption"), {
        type: "success",
      });
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("functionDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };

  const handleCreation = () => {
    setShowPreSelection(true);
  };

  const handleNewToolSelect = (toolType = "api") => {
    setShowPreSelection(false);

    const baseValues = {
      name: "",
      description: "",
      scope: null,
      parameters: {
        type: "object",
        properties: {
          location: {
            type: "string",
            description: "The location, e.g. Melbourne, VIC",
          },
          unit: {
            type: "string",
            description: "The measurement unit",
            enum: ["celsius"],
          },
        },
        required: ["location"],
      },
    };

    let initialValues = { ...baseValues };

    switch (toolType) {
      case "mcp":
        initialValues = {
          ...baseValues,
          url: "",
          authorisationType: "NONE",
          headers: {},
          staticArgs: {},
          isMCPServer: true,
        };
        break;
      case "database":
        initialValues = {
          ...baseValues,
          dbScript: "",
          isDatabaseScript: true,
        };
        break;
      case "code":
        initialValues = {
          name: "",
          description: "",
          isCodeTemplate: true,
          scope: null,
          parameters: baseValues.parameters,
        };
        break;
      case "html":
        initialValues = {
          name: "",
          description: "",
          htmlScript: "",
          htmlUrl: "",
          htmlFormOnSuccessfulSubmitMessage: "",
          htmlFormOnFailedSubmitMessage: "",
          isHtmlFn: true,
          isHtmlRedirectFn: true,
          scope: null,
          paramsAsPath: false,
        };
        break;
      case "chat":
        initialValues = {
          name: "",
          description: "",
          htmlScript: "",
          htmlUrl: "",
          htmlFormOnSuccessfulSubmitMessage: "",
          htmlFormOnFailedSubmitMessage: "",
          isChatFn: true,
          isDatabaseScript: false,
          scope: null,
          paramsAsPath: false,
          chatScript: "",
          chatAction: "suggestion",
        };
        break;
      case "skill":
        initialValues = {
          name: "",
          description: "",
          agentSkillScript: "",
          isAgentSkill: true,
          isChatFn: false,
          isDatabaseScript: false,
        };
        break;
      default:
        // api
        initialValues = {
          ...baseValues,
          requestType: "GET",
          authorisationType: "NONE",
          url: "",
          model: "speed",
          headers: {},
          staticArgs: {},
          isDatabaseScript: false,
          paramsAsPath: false,
          graphqlQuery: "",
          graphqlMutation: "",
          graphqlOperationName: "",
        };
    }

    history.push(`/workspaces/${getActiveWorkspace()?.id}/tools/new`, {
      toolConfig: initialValues,
    });
  };

  const handleTemplateToolSelect = async (tool) => {
    setShowPreSelection(false);
    setSelectedGlobalTool(tool);

    // Agent skills don't require authorisation wizard
    if (tool?.requiresAuth && !tool?.isAgentSkill) {
      setShowHiringTemplate(true);
    } else {
      await populateFormFromTemplateTool(tool);
    }
  };

  const populateFormFromTemplateTool = async (tool, hiringData = null) => {
    const toolData = await HiringTemplatesAPI.fetchCompleteToolData(tool.id);

    if (!toolData) {
      AppToasty.show(t("settings.list.couldNotLoadToolData"), {
        type: "danger",
      });
      setSelectedGlobalTool(null);
      return;
    }

    let parsedTool = { ...toolData };
    parsedTool.id = undefined;
    parsedTool.workspaceID = getActiveWorkspace()?.id;
    delete parsedTool.parentToolID;
    delete parsedTool.createdAt;
    delete parsedTool.updatedAt;
    delete parsedTool._version;
    delete parsedTool._deleted;
    delete parsedTool._lastChangedAt;
    delete parsedTool.__typename;

    if (typeof parsedTool.parameters === "string") {
      try {
        parsedTool.parameters = JSON.parse(parsedTool.parameters);
      } catch (e) {
        parsedTool.parameters = {};
      }
    }
    if (typeof parsedTool.headers === "string") {
      try {
        parsedTool.headers = JSON.parse(parsedTool.headers);
      } catch (e) {
        parsedTool.headers = {};
      }
    }
    if (typeof parsedTool.staticArgs === "string") {
      try {
        parsedTool.staticArgs = JSON.parse(parsedTool.staticArgs);
      } catch (e) {
        parsedTool.staticArgs = {};
      }
    }

    if (hiringData?.authorisationID) {
      parsedTool.authorisationID = hiringData.authorisationID;
      const authTypeMap = {
        apikey: "APIKEY",
        bearer: "BEARER",
        oauth: "OAUTH",
        basic: "BASIC",
      };
      if (hiringData?.authorisationType) {
        parsedTool.authorisationType =
          authTypeMap[hiringData.authorisationType] ||
          parsedTool.authorisationType?.toUpperCase();
      }
    }

    setSelectedGlobalTool(null);
    history.push(`/workspaces/${getActiveWorkspace()?.id}/tools/new`, {
      toolConfig: parsedTool,
    });
  };

  const handleHiringTemplateComplete = (data) => {
    if (selectedGlobalTool) {
      populateFormFromTemplateTool(selectedGlobalTool, data);
    }
    setShowHiringTemplate(false);
  };

  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancel")}
        caption={t("settings.list.deleteFunctionConfirmation")}
        subCaption={t("settings.list.deleteFunctionConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleFunctionItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);

  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deleteFunctionConfirmation"),
        })}
        subCaption={t("settings.list.deleteFunctionConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedFunctionIds]);

  return (
    <View
      style={{
        paddingLeft: 10,
        flex: 1,
        width: showSideMenu() ? width - 220 : "100%",
        minWidth: "100%",
        overflow: isLoading && "hidden",
      }}
    >
      {getPlanConfig()?.isFunctionsEnabled ? (
        <>
          {isLoading ? (
            <ToothFairyLoadingPlaceholder
              backgroundColor={currentTheme?.theme?.softBackgroundColor}
              visible={true}
              containerStyle={{
                backgroundColor: currentTheme?.theme?.transparent,
                minHeight: 250,
              }}
              caption={t("settings.list.loadingFunctions")}
            />
          ) : (
            <>
              {displayedFunctions.length > 0 ? (
                <AppTable
                  isSelectable
                  showPagination
                  itemsPerPage={50}
                  hideSearch={false}
                  searchPlaceholder={t("settings.toolbar.searchFunctions")}
                  onSelectionChange={(selectedItems) => {
                    setSelectedFunctionIds(
                      selectedItems?.map((u) => u?.id) || [],
                    );
                  }}
                  bulkActions={[
                    {
                      id: "DELETE",
                      label: t("settings.list.delete"),
                      icon: AppAssets.icons.delete,
                      isDestructive: true,
                    },
                  ]}
                  onBulkActionPress={(id) => {
                    if (id === "DELETE" && selectedFunctionIds?.length > 0)
                      setShowBulkDeleteChoice(true);
                  }}
                  actionButtons
                  showDropDownMenuForActions
                  mainActionLabel={t("settings.list.edit")}
                  onClickMainAction={(item, index) => {
                    handleFunctionItemSelection({ item, index });
                  }}
                  onActionItemPress={(id, item, index) => {
                    if (id === "EDIT") {
                      handleFunctionItemSelection({ item, index });
                    } else if (id === "DUPLICATE") {
                      handleDuplication(item?.id);
                    } else if (id === "DELETE") {
                      setSelectedItem(item);
                      setSelectedIndex(index);
                      setSelectedFunctionId(item?.id);
                      setShowChoice(true);
                    }
                  }}
                  actions={[
                    {
                      label: t("settings.list.edit"),
                      id: "EDIT",
                      icon: AppAssets.icons.edit,
                    },
                    {
                      label: t("settings.list.duplicate"),
                      id: "DUPLICATE",
                      icon: AppAssets.icons.duplicate,
                    },
                    {
                      label: t("settings.list.delete"),
                      id: "DELETE",
                      icon: AppAssets.icons.delete,
                    },
                  ]}
                  style={{
                    zIndex: -1,
                    marginTop: 15,
                    height:
                      displayedFunctions?.length >= 50
                        ? height - 75
                        : height - 55,
                    paddingRight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                  headers={columns}
                  items={displayedFunctions?.map((u) => {
                    return {
                      id: u?.id,
                      name: u?.name
                        ? u?.functionVersion
                          ? `${u.name} (v${u.functionVersion})`
                          : u.name
                        : " ",
                      description:
                        u?.description ||
                        t("settings.list.noDescriptionProvided"),
                      requestType: u?.isDatabaseScript
                        ? "DB"
                        : u?.isMCPServer
                        ? "MCP"
                        : u?.isHtmlFn
                        ? "HTML"
                        : u?.isChatFn
                        ? "CHAT"
                        : u?.isCodeTemplate
                        ? "CODE"
                        : u?.isAgentSkill
                        ? "SKILL"
                        : u?.requestType === "GRAPHQL_QUERY"
                        ? "GQL QUERY"
                        : u?.requestType === "GRAPHQL_MUTATION"
                        ? "GQL MUTATION"
                        : u?.requestType?.toUpperCase() || " ",
                      // url: u?.isDatabaseScript
                      //   ? u?.dbScript
                      //   : u?.isHtmlFn
                      //   ? u?.htmlUrl
                      //   : u?.url || " ",
                      actions: " ",
                    };
                  })}
                >
                  <ToothFairyButton
                    title={t("settings.toolbar.newTool")}
                    onPress={() => handleCreation("NEW_TOOL_PRESELECTION")}
                  />
                </AppTable>
              ) : (
                <AppPlaceholderView
                  wrapperStyle={{
                    width: "100%",
                    height: "100%",
                    marginVertical: 60,
                    marginHorizontal: "auto",
                    zIndex: -1,
                    flex: 1,
                  }}
                  size={30}
                  icon={AppAssets.icons.functions}
                  mainCaption={t("settings.list.noFunctionsFound")}
                  subCaption={t("settings.list.noFunctionsFoundSub")}
                  actionLabel={t("settings.toolbar.newTool")}
                  onActionPress={() => handleCreation("NEW_TOOL_PRESELECTION")}
                />
              )}
            </>
          )}
        </>
      ) : (
        <AppPlaceholderView
          wrapperStyle={{
            width: "100%",
            height: "100%",
            marginVertical: 60,
            marginHorizontal: "auto",
            flex: 1,
          }}
          size={30}
          icon={AppAssets.icons.accessDenied}
          mainCaption={t("settings.list.toolsNotAvailable")}
          subCaption={t("settings.list.upgradeToEnableTools")}
        />
      )}
      {_coreChoice}
      {_bulkDeleteChoice}
      {showPreSelection && (
        <FunctionPreSelection
          onNewToolSelect={handleNewToolSelect}
          onTemplateToolSelect={handleTemplateToolSelect}
          onClose={() => setShowPreSelection(false)}
        />
      )}
      {showHiringTemplate && selectedGlobalTool && (
        <FunctionHiringTemplate
          tool={selectedGlobalTool}
          onComplete={handleHiringTemplateComplete}
          onCancel={() => {
            setShowHiringTemplate(false);
            setSelectedGlobalTool(null);
          }}
        />
      )}
    </View>
  );
};

export default FunctionsList;
